import type { DataIdentifier, EntityAttribute } from '@onefootprint/request-types/dashboard';
import type { EntityVault } from '@/types/vault';

const cardOrder: Record<string, number> = {
  issuer: 1,
  name: 2,
  number: 3,
  expiration: 4,
  cvc: 5,
  billing_address: 6,
  fingerprint: 7,
};

const bankOrder: Record<string, number> = {
  link_id: 1,
  institution_id: 2,
  institution_name: 3,
  account_type: 4,
  name: 5,
  account_last4: 6,
  ach_account_number: 7,
  ach_routing_number: 8,
  owners: 9,
  fingerprint: 10,
};

const filterCards = (attributes: EntityAttribute[], search: string | null | undefined) => {
  const hiddenAttributes = ['expiration_month', 'expiration_year', 'number_last4'];
  return attributes.filter(
    attr =>
      attr.identifier.includes(`card.${search}`) &&
      !hiddenAttributes.some(hiddenAttr => attr.identifier.includes(hiddenAttr)),
  );
};

const filterBankAccounts = (attributes: EntityAttribute[], search: string | null | undefined) => {
  return attributes.filter(attr => attr.identifier.includes(`bank.${search}`));
};

export const getDI = (target: string) => {
  if (target.startsWith('card')) {
    const cardAlias = target.split('.')[1];
    return `di.${target.replace(`${cardAlias}.`, 'verbose.')}`;
  }
  if (target.startsWith('custom')) {
    return target;
  }
  return `di.${target}`;
};

const sortCardDIs = (attributes: EntityAttribute[]) =>
  attributes.sort((a, b) => {
    const aParts = a.identifier.split('.');
    const bParts = b.identifier.split('.');
    const aKey = aParts[2] || '';
    const bKey = bParts[2] || '';

    const keyComparison = cardOrder[aKey] - cardOrder[bKey];
    if (keyComparison !== 0) {
      return keyComparison;
    }

    // If the third part is the same, compare based on the fourth part
    const aSubKey = aParts[3] || '';
    const bSubKey = bParts[3] || '';
    return aSubKey.localeCompare(bSubKey);
  });

const sortBankDIs = (attributes: EntityAttribute[]) =>
  attributes.sort((a, b) => {
    const aParts = a.identifier.split('.');
    const bParts = b.identifier.split('.');
    const aKey = aParts[2] || '';
    const bKey = bParts[2] || '';
    return bankOrder[aKey] - bankOrder[bKey];
  });

export const getCustomDIs = (vaultData: EntityVault, entityData: EntityAttribute[]) => {
  const attributes = Object.keys(vaultData);
  const filtered = attributes
    .filter(attr => attr.startsWith('custom.')) // custom documents use the `document.custom.*` prefix, so they're already excluded
    .filter(attr => entityData.some(data => data.identifier === attr)) // To remove versioned dis (eg document.custom.a:123)
    .sort((a, b) => a.localeCompare(b));
  return filtered as DataIdentifier[];
};

export const attributesToDIs = (attributes: EntityAttribute[]) => {
  return attributes.map(attr => attr.identifier);
};

export const getCardDis = (attributes: EntityAttribute[], search: string | null | undefined) => {
  const filtered = filterCards(attributes, search);
  const sorted = sortCardDIs(filtered);
  const dis = attributesToDIs(sorted);
  return dis;
};

export const getBankDis = (attributes: EntityAttribute[], search: string | null | undefined) => {
  const filtered = filterBankAccounts(attributes, search);
  const sorted = sortBankDIs(filtered);
  const dis = attributesToDIs(sorted);
  return dis;
};

export default getDI;
